import { useCallback, useMemo, useState } from 'react';
import {
  CARD_TYPE_ORDER,
  BATTLE_TYPE_ORDER,
  RARITY_ORDER,
} from '../services/cardCatalog';

/** Sentinel for "no selection" on every dropdown. */
export const ALL = 'all';

/** The filter dimensions, in the order their dropdowns appear. */
const DIMENSIONS = ['cardType', 'battleType', 'character', 'levelRound', 'rarity'];

/** Order `values` by `order`, appending anything unrecognized alphabetically. */
function byDisplayOrder(values, order) {
  const known = order.filter((v) => values.has(v));
  const extra = [...values].filter((v) => !order.includes(v)).sort();
  return [...known, ...extra];
}

/**
 * Faceted, client-side card filtering over the full catalog.
 *
 * Two behaviours are worth knowing about:
 *
 * 1. **Faceted narrowing.** Each dropdown only offers values that actually
 *    exist given the *other* active filters — so "Scene" never lists Level 7,
 *    and picking a character narrows the levels to the ones that character was
 *    printed at. A dimension's own selection is excluded when computing its
 *    options, otherwise every dropdown would collapse to the single value you
 *    just picked and you could never change your mind.
 *
 * 2. **Search is excluded from the narrowing.** Typing narrows the *results*
 *    but never the dropdown options — otherwise the options would churn under
 *    the cursor on every keystroke.
 *
 * Selections are never auto-cleared when they stop matching. A zero-result
 * combination shows zero results, which is honest; silently resetting a filter
 * the user set is not.
 */
export function useCardFilters(catalog) {
  const [searchQuery, setSearchQuery] = useState('');
  const [cardTypeFilter, setCardTypeFilter] = useState(ALL);
  const [battleTypeFilter, setBattleTypeFilter] = useState(ALL);
  const [characterFilter, setCharacterFilter] = useState(ALL);
  const [levelFilter, setLevelFilter] = useState(ALL);
  // Scenes have a field-round requirement instead of a level; the Level slot
  // becomes a Round slot while Card Type is Scene.
  const [roundFilter, setRoundFilter] = useState(ALL);
  const [rarityFilter, setRarityFilter] = useState(ALL);

  const sceneMode = cardTypeFilter === 'Scene';

  // One predicate per dropdown, so the narrowing below can drop exactly one
  // dimension at a time.
  const predicates = useMemo(
    () => ({
      cardType: (c) => cardTypeFilter === ALL || c.cardTypeLabel === cardTypeFilter,
      // Scenes carry no battle type, so the Type filter simply doesn't apply.
      battleType: (c) =>
        sceneMode || battleTypeFilter === ALL || c.battleTypeLabel === battleTypeFilter,
      character: (c) => characterFilter === ALL || c.characterLabel === characterFilter,
      levelRound: (c) =>
        sceneMode
          ? roundFilter === ALL || c.roundValue === Number(roundFilter)
          : levelFilter === ALL || c.levelValue === Number(levelFilter),
      rarity: (c) => rarityFilter === ALL || c.rarityLabel === rarityFilter,
    }),
    [cardTypeFilter, battleTypeFilter, characterFilter, levelFilter, roundFilter, rarityFilter, sceneMode]
  );

  /** Cards matching every dimension except the named one (its option pool). */
  const matchingExcept = useMemo(
    () => (except) =>
      catalog.filter((c) => DIMENSIONS.every((d) => d === except || predicates[d](c))),
    [catalog, predicates]
  );

  const availableCardTypes = useMemo(
    () =>
      byDisplayOrder(
        new Set(matchingExcept('cardType').map((c) => c.cardTypeLabel).filter(Boolean)),
        CARD_TYPE_ORDER
      ),
    [matchingExcept]
  );

  const availableBattleTypes = useMemo(
    () =>
      byDisplayOrder(
        new Set(matchingExcept('battleType').map((c) => c.battleTypeLabel).filter(Boolean)),
        BATTLE_TYPE_ORDER
      ),
    [matchingExcept]
  );

  const availableCharacters = useMemo(
    () =>
      [...new Set(matchingExcept('character').map((c) => c.characterLabel).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b)),
    [matchingExcept]
  );

  const availableLevels = useMemo(
    () =>
      [...new Set(matchingExcept('levelRound').map((c) => c.levelValue).filter((l) => l != null))]
        .sort((a, b) => a - b),
    [matchingExcept]
  );

  const availableRounds = useMemo(
    () =>
      [...new Set(matchingExcept('levelRound').map((c) => c.roundValue).filter((r) => r != null))]
        .sort((a, b) => a - b),
    [matchingExcept]
  );

  const availableRarities = useMemo(
    () =>
      byDisplayOrder(
        new Set(matchingExcept('rarity').map((c) => c.rarityLabel).filter(Boolean)),
        RARITY_ORDER
      ),
    [matchingExcept]
  );

  const filteredCards = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return catalog.filter((card) => {
      // searchText is precomputed in the catalog (name + number + character +
      // effect), so this stays a single substring test per card.
      if (query && !card.searchText.includes(query)) return false;
      return DIMENSIONS.every((d) => predicates[d](card));
    });
  }, [catalog, predicates, searchQuery]);

  const activeFilterCount =
    (cardTypeFilter !== ALL ? 1 : 0) +
    (!sceneMode && battleTypeFilter !== ALL ? 1 : 0) +
    (characterFilter !== ALL ? 1 : 0) +
    (sceneMode ? (roundFilter !== ALL ? 1 : 0) : levelFilter !== ALL ? 1 : 0) +
    (rarityFilter !== ALL ? 1 : 0);

  const clearFilters = useCallback(() => {
    setCardTypeFilter(ALL);
    setBattleTypeFilter(ALL);
    setCharacterFilter(ALL);
    setLevelFilter(ALL);
    setRoundFilter(ALL);
    setRarityFilter(ALL);
  }, []);

  const resetAll = useCallback(() => {
    setSearchQuery('');
    clearFilters();
  }, [clearFilters]);

  return {
    // Values
    searchQuery,
    cardTypeFilter,
    battleTypeFilter,
    characterFilter,
    levelFilter,
    roundFilter,
    rarityFilter,
    sceneMode,

    // Setters
    setSearchQuery,
    setCardTypeFilter,
    setBattleTypeFilter,
    setCharacterFilter,
    setLevelFilter,
    setRoundFilter,
    setRarityFilter,

    // Faceted option lists
    availableCardTypes,
    availableBattleTypes,
    availableCharacters,
    availableLevels,
    availableRounds,
    availableRarities,

    // Results
    filteredCards,
    activeFilterCount,
    clearFilters,
    resetAll,
  };
}

export default useCardFilters;
